import {
  GenericItem,
  GroupHeader,
  InputItemGroup,
  FlatGroupedItem,
  ItemsProp,
} from '../types';
import isHiddenMenuItem from './isHiddenMenuItem';

/**
 * gets the sum of all items in all previous groups, in order
 */
const getAllPreviousGroupCounts = <TItem extends GenericItem>(
  groupedItems: InputItemGroup<TItem>[],
) => {
  const allCounts = groupedItems.reduce(
    (acc, curr) => {
      const currentCount = curr.items.length;

      acc.counts.push(acc.total);
      acc.total += currentCount;

      return acc;
    },
    { counts: [] as number[], total: 0 },
  );

  return allCounts.counts;
};

export const getStartingIndices = <TItem extends GenericItem>(
  groupedItems: InputItemGroup<TItem>[],
) => {
  const groupCounts = getAllPreviousGroupCounts(groupedItems);
  return groupedItems.reduce((indices, _, index) => {
    if (index === 0) {
      // the first group will always map to the first item
      return indices.set(0, 0);
    }

    return indices.set(index, groupCounts[index]);
  }, new Map<number, number>());
};

/**
 * Strips the grouping out of dropdown items, transforming them into items, including a dummy item for the group header.
 * THESE ARE NOT REFERENCE EQUAL TO THE PROVIDED ITEMS.
 * NOTE: Pinned groups should be placed first in the groupedItems array to appear at the top.
 * @param groupedItems The grouped dropdown items
 * @param startingIndices The indicies that the group should start at, in the format [groupIndex, firstItemIndex]
 * @returns flattened items without any context of grouping
 */
export const flattenGroupItemsToNonDownshift = <TItem extends GenericItem>(
  groupedItems: InputItemGroup<TItem>[],
  startingIndices: Map<number, number>,
): (GroupHeader | FlatGroupedItem<TItem>)[] =>
  groupedItems.reduce<(GroupHeader | FlatGroupedItem<TItem>)[]>(
    (prev, curr, groupIndex) => {
      const groupKey = `group${groupIndex}`;
      const nonHiddenItems = curr.items.filter(
        (item) => !isHiddenMenuItem(item),
      );
      const groupHeader: GroupHeader = {
        label: curr.label,
        groupKey,
        isGroupHeader: true,
        itemCount: nonHiddenItems.length,
        pinned: curr.pinned,
      };

      prev.push(groupHeader);

      curr.items.forEach((item, itemIndex) =>
        prev.push({
          ...item,
          index: (startingIndices.get(groupIndex) ?? 0) + itemIndex,
          groupKey,
        }),
      );

      return prev;
    },
    [],
  );

export const areGroupedItems = <TItem extends GenericItem>(
  maybeGroupedItems: ItemsProp<TItem>,
): maybeGroupedItems is InputItemGroup<TItem>[] => {
  const unsafeMaybeGroupedItems = maybeGroupedItems as InputItemGroup<TItem>[];
  return unsafeMaybeGroupedItems[0] && !!unsafeMaybeGroupedItems[0].items;
};

/**
 * Strips the grouping out of dropdown items
 * @returns flattened items without any context of grouping
 * @param maybeGroupedItems
 */
export const flattenGroupItemsToDownshift = <TItem extends GenericItem>(
  maybeGroupedItems: ItemsProp<TItem>,
): TItem[] =>
  areGroupedItems(maybeGroupedItems)
    ? maybeGroupedItems.reduce<TItem[]>((result, group) => {
        result.push(...group.items);
        return result;
      }, [])
    : maybeGroupedItems;

export const convertGroupKeyToIndex = (groupKey: string) => {
  const prefix = 'group';
  if (groupKey.startsWith(prefix)) {
    const numPart = groupKey.slice(prefix.length);
    const num = Number(numPart);
    return isNaN(num) ? null : num;
  }
  return null;
};
